import { useEffect, useRef } from 'react';
import type { EmailTemplate } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/email-template/v1/emailtemplate.pb';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { EmailTemplateQueries } from '@frontend/api-email-template';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { SpinningLoader, Text } from '@frontend/design-system';
import { NEW_CAMPAIGN_ID } from '../../constants';
import { useBulkNavigator } from '../../hooks/use-bulk-navigator';
import { BulkEmailTemplateCard } from './bulk-email-template-card';
import { TEMPLATE_GRID_HEIGHT, type TemplateFilter } from './constants';
import { TemplateToolbar } from './template-toolbar';

type Props = {
  activeFilter: TemplateFilter;
  hasMore: boolean | undefined;
  isFetchingMore: boolean;
  isLoading: boolean;
  loadMore: () => void;
  onCustomTemplateClick: (isEditing?: boolean, templateId?: string) => void;
  onDelete: (templateId: string) => void;
  onDuplicate: (template: EmailTemplate) => void;
  onFilterChange: (filter: TemplateFilter) => void;
  onSearchChange: (query: string) => void;
  onView: (template: EmailTemplate) => void;
  onWeaveTemplateClick: (templateId?: string) => void;
  searchQuery: string;
  visibleTemplates: EmailTemplate[];
};

export const TemplateSection = ({
  activeFilter,
  hasMore,
  isFetchingMore,
  isLoading,
  loadMore,
  onCustomTemplateClick,
  onDelete,
  onDuplicate,
  onFilterChange,
  onSearchChange,
  onView,
  onWeaveTemplateClick,
  searchQuery,
  visibleTemplates,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const navigateTo = useBulkNavigator(CampaignType.EMAIL);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollContainerRef.current;
    if (!sentinel || !container) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: container, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <div css={sectionStyle}>
      <TemplateToolbar
        activeFilter={activeFilter}
        onCreateTemplate={() => navigateTo.composer({ templateId: NEW_CAMPAIGN_ID, startFresh: true })}
        onFilterChange={onFilterChange}
        onSearchChange={onSearchChange}
        searchQuery={searchQuery}
      />

      <div ref={scrollContainerRef} css={[gridContainerStyle, { height: TEMPLATE_GRID_HEIGHT }]}>
        {isLoading ? (
          <div css={[centeredStyle, initialLoaderStyle]}>
            <SpinningLoader />
            <Text color='subdued' size='small'>
              {t('Loading templates...')}
            </Text>
          </div>
        ) : visibleTemplates.length === 0 ? (
          <div css={centeredStyle}>
            <Text color='subdued'>{searchQuery ? t('No templates match your search.') : t('No templates found.')}</Text>
          </div>
        ) : (
          <>
            <div css={gridStyle}>
              {visibleTemplates.map((template) => (
                <BulkEmailTemplateCard
                  key={template.id}
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                  onView={onView}
                  onTemplateClick={(isEditing) =>
                    EmailTemplateQueries.isSystemEmailTemplate(template)
                      ? onWeaveTemplateClick(template.id)
                      : onCustomTemplateClick(isEditing, template.id)
                  }
                  template={template}
                />
              ))}
            </div>

            {isFetchingMore && (
              <div css={fetchMoreLoaderStyle}>
                <SpinningLoader />
                <Text color='subdued' size='small'>
                  {t('Loading more templates...')}
                </Text>
              </div>
            )}

            {hasMore && <div ref={sentinelRef} css={{ height: 1 }} />}

            {!hasMore && !isLoading && visibleTemplates.length > 0 && (
              <div css={endOfListStyle}>
                <Text color='subdued'>
                  {'🎉 '}
                  {t('You have reached the end')}
                </Text>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const sectionStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: theme.spacing(2),
  padding: '24px',
};

const gridContainerStyle = {
  overflowY: 'auto' as const,
};

const gridStyle = {
  display: 'grid',
  gap: theme.spacing(2),
  alignContent: 'start' as const,
  gridTemplateColumns: '1fr',
  [`@media (min-width: ${breakpoints.small.min}px)`]: {
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 340px))',
  },
};

const fetchMoreLoaderStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
};

const endOfListStyle = {
  display: 'flex',
  justifyContent: 'center',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
};

const centeredStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  minHeight: theme.spacing(20),
};

const initialLoaderStyle = {
  flexDirection: 'column' as const,
  gap: theme.spacing(1),
};
